/**
 * Final rail step: C1 qualifiers and Strengths / Development feedback slots.
 * Content is unchanged from the original modal; only the placement moved.
 */
import React from 'react';
import { Text, View } from 'react-native';

import { C1_QUALIFIERS, getLevelForm, type IcfFormLevel } from '@/data/icfFormStatements';
import { useTranslation } from '@/hooks/useTranslation';
import type { EngagementLanguage, SessionObservationForm, SofFeedback } from '@/types/mentorEngagements';
import { ActionButton, SegmentedField, TextField, mentorStyles } from '../shared';
import type { SofReviewState } from '../sofReview';

interface Props {
  level: IcfFormLevel;
  language: EngagementLanguage;
  form: SessionObservationForm;
  state: SofReviewState;
  disabled: boolean;
  locked: boolean;
  saving: boolean;
  setState: React.Dispatch<React.SetStateAction<SofReviewState>>;
}

export function SofWrapUpStep({ level, language, form, state, disabled, locked, saving, setState }: Props) {
  const { t } = useTranslation();
  const catalog = getLevelForm(level);
  const c1Options = [
    { value: 'observed' as const, label: t('mentorEngagements.review.observed') },
    { value: 'not_observed' as const, label: t('mentorEngagements.review.notObserved') },
  ];
  const feedback = state.feedback ?? { strengths: [], development: [] };
  const updateFeedback = (side: keyof SofFeedback, index: number, patch: Partial<SofFeedback['strengths'][number]>) => {
    setState(current => {
      const next = current.feedback ?? { strengths: [], development: [] };
      return { ...current, feedback: { ...next, [side]: next[side].map((slot, i) => i === index ? { ...slot, ...patch } : slot) } };
    });
  };
  return (
    <View testID="sof_review_wrapup" style={{ gap: 24, minWidth: 0 }}>
      <View style={{ gap: 10 }}>
        <Text style={mentorStyles.panelTitle}>{t('mentorEngagements.review.c1')}</Text>
        <Text style={mentorStyles.mutedText}>{t('mentorEngagements.review.c1Note')}</Text>
        {C1_QUALIFIERS.map(qualifier => {
          const key = qualifier.key === 'q1' ? 'c1_q1' : 'c1_q2';
          return <View key={key} style={{ gap: 6 }}>
            <Text style={mentorStyles.bodyText}>{language === 'tr' ? qualifier.text_tr : qualifier.text_en}</Text>
            <SegmentedField options={c1Options} value={state.c1[key] ?? form[key]} disabled={disabled} onChange={value => setState(current => ({ ...current, c1: { ...current.c1, [key]: value } }))} />
            <Text style={mentorStyles.mutedText}>{t(`mentorEngagements.review.${state.c1[key] ? 'confirmed' : 'storedValue'}`)} · {t('mentorEngagements.review.original')}: {form[key] ? t(`mentorEngagements.review.${form[key] === 'observed' ? 'observed' : 'notObserved'}`) : t('mentorEngagements.review.unrated')}</Text>
          </View>;
        })}
        <View style={mentorStyles.row}><TextField label={t('mentorEngagements.review.comment')} value={state.c1.c1_comment ?? form.c1_comment ?? ''} editable={!disabled} onChangeText={value => setState(current => ({ ...current, c1: { ...current.c1, c1_comment: value } }))} multiline /></View>
      </View>
      {(['strengths', 'development'] as const).map(side => <View key={side} style={{ gap: 10 }}>
        <Text style={mentorStyles.panelTitle}>{t(`mentorEngagements.review.${side}`)}</Text>
        {feedback[side].map((slot, index) => <View key={index} style={{ gap: 8, minWidth: 0 }}>
          <Text style={mentorStyles.fieldLabel}>{t('mentorEngagements.review.competency')}</Text>
          <SegmentedField options={catalog.competencies.map(item => ({ value: item.code, label: item.code }))} value={slot.competency_code} disabled={disabled} onChange={value => updateFeedback(side, index, { competency_code: value })} />
          <View style={mentorStyles.row}><TextField label={t('mentorEngagements.review.statement')} value={slot.statement} editable={!disabled} onChangeText={value => updateFeedback(side, index, { statement: value })} multiline /></View>
          <View style={mentorStyles.row}><TextField label={t('mentorEngagements.review.comment')} value={slot.comments} editable={!disabled} onChangeText={value => updateFeedback(side, index, { comments: value })} multiline /></View>
          {!locked ? <ActionButton label={t('mentorEngagements.review.removeFeedback')} disabled={saving} onPress={() => setState(current => ({ ...current, feedback: { ...feedback, [side]: feedback[side].filter((_, i) => i !== index) } }))} /> : null}
        </View>)}
        {!locked && feedback[side].length < 3 ? <ActionButton label={t('mentorEngagements.review.addFeedback')} disabled={saving} onPress={() => setState(current => ({ ...current, feedback: { ...feedback, [side]: [...feedback[side], { competency_code: null, statement: '', comments: '' }] } }))} /> : null}
      </View>)}
    </View>
  );
}
